import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { queryKeys } from '../lib/queryKeys';
import { useAuthStore } from '../stores/authStore';
import { is2FARequired, isLoginSuccess } from '../types/auth';
import type { AdminLoginRequest, Admin2FAVerifyRequest, Admin2FASetupConfirmRequest } from '../types/auth';

export function useLogin() {
  const { setTokens, setUser, setTempToken } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: AdminLoginRequest) => authApi.login(data),
    onSuccess: (result) => {
      if (isLoginSuccess(result)) {
        setTokens(result.access_token, result.refresh_token);
        setUser(result.admin_user);
        navigate('/');
      } else if (is2FARequired(result)) {
        setTempToken(result.temp_token);
        navigate('/login/2fa');
      }
    },
    onError: () => {
      message.error('Invalid credentials');
    },
  });
}

export function useVerify2FA() {
  const { setTokens, setUser } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: Admin2FAVerifyRequest) => authApi.verify2fa(data),
    onSuccess: (result) => {
      setTokens(result.access_token, result.refresh_token);
      setUser(result.admin_user);
      navigate('/');
    },
    onError: () => {
      message.error('Invalid 2FA code');
    },
  });
}

export function useSetup2FA() {
  return useMutation({
    mutationFn: () => authApi.setup2fa(),
  });
}

export function useConfirm2FA() {
  return useMutation({
    mutationFn: (data: Admin2FASetupConfirmRequest) => authApi.confirm2fa(data),
    onSuccess: () => {
      message.success('2FA enabled successfully');
    },
  });
}

export function useLogout() {
  const { clear } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      clear();
      queryClient.clear();
      navigate('/login');
    },
  });
}

export function useSessions() {
  return useQuery({
    queryKey: queryKeys.auth.sessions,
    queryFn: () => authApi.getSessions(),
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => authApi.revokeSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.sessions });
      message.success('Session revoked');
    },
  });
}
